import React, { useState, useEffect, useCallback } from 'react';
import { useOfflineStatus } from '../hooks/useOfflineStatus';
import { skipWaiting } from '../serviceWorkerRegistration';

export const OfflineIndicator = React.memo(function OfflineIndicator() {
  const { isOffline, cacheSize } = useOfflineStatus();
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [showCacheInfo, setShowCacheInfo] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handleUpdateAvailable = () => {
      setUpdateAvailable(true);
      setDismissed(false);
    };

    window.addEventListener('sw-update-available', handleUpdateAvailable);
    return () => {
      window.removeEventListener('sw-update-available', handleUpdateAvailable);
    };
  }, []);

  const handleRefresh = useCallback(() => {
    skipWaiting();
    window.location.reload();
  }, []);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  // Don't show anything if online and no update
  if (!isOffline && !updateAvailable) {
    return null;
  }

  // Don't show if dismissed
  if (dismissed && !isOffline) {
    return null;
  }

  return (
    <>
      {/* Offline banner */}
      {isOffline && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 20px',
            background: 'var(--warning)',
            color: '#000',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            zIndex: 10000,
            fontSize: '13px',
            fontWeight: 500,
          }}
        >
          <span style={{ fontSize: '18px' }}>!</span>
          <div>
            <div style={{ fontWeight: 'bold' }}>You're offline</div>
            <div style={{ fontSize: '11px', opacity: 0.8 }}>
              Some features may be limited. Cached data: {cacheSize}
            </div>
          </div>
          <button
            onClick={() => setShowCacheInfo(!showCacheInfo)}
            style={{
              background: 'rgba(0,0,0,0.1)',
              border: 'none',
              borderRadius: '4px',
              padding: '4px 8px',
              cursor: 'pointer',
              fontSize: '11px',
              color: '#000',
            }}
          >
            {showCacheInfo ? 'Hide' : 'Info'}
          </button>
        </div>
      )}

      {/* Update available banner */}
      {updateAvailable && !isOffline && !dismissed && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 20px',
            background: 'var(--accent)',
            color: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            zIndex: 10000,
            fontSize: '13px',
            fontWeight: 500,
          }}
        >
          <span style={{ fontSize: '18px' }}>*</span>
          <div>
            <div style={{ fontWeight: 'bold' }}>Update available</div>
            <div style={{ fontSize: '11px', opacity: 0.9 }}>
              A new version of Atlas IDE is ready
            </div>
          </div>
          <button
            onClick={handleRefresh}
            style={{
              background: 'white',
              color: 'var(--accent)',
              border: 'none',
              borderRadius: '4px',
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
            }}
          >
            Refresh
          </button>
          <button
            onClick={handleDismiss}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '4px',
              padding: '4px 8px',
              cursor: 'pointer',
              fontSize: '12px',
              color: 'white',
            }}
          >
            Later
          </button>
        </div>
      )}

      {/* Cache info modal */}
      {showCacheInfo && isOffline && (
        <div
          style={{
            position: 'fixed',
            bottom: '90px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '16px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            zIndex: 10000,
            fontSize: '12px',
            color: 'var(--text)',
            maxWidth: '300px',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Offline Mode Info</div>
          <ul style={{ margin: 0, paddingLeft: '16px', lineHeight: 1.6 }}>
            <li>Cached assets: {cacheSize}</li>
            <li>Editor works fully offline</li>
            <li>Preview may be limited</li>
            <li>API calls will use cached data</li>
            <li>Changes are saved locally</li>
          </ul>
          <div style={{ marginTop: '12px', fontSize: '11px', color: 'var(--muted)' }}>
            Tip: Export your project before going offline to ensure all files are saved.
          </div>
        </div>
      )}
    </>
  );
});

export default OfflineIndicator;
